import React, { useState, useEffect } from 'react';
import { useUser } from '../../auth/AuthPage/UserContext';
import LoginGate from '../../common/LoginGate/LoginGate';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import {
    XP_PER_MM_TRADE, xpForLevel, levelFromXp,
    BORDERS, SHOP_BORDER, VERIFIED_BORDER, ADMIN_BORDER, borderForUser, isVerified,
} from '../../../utils/experience';
import './Experience.css';

/**
 * EXPERIENCE - your level, how it grows, and the borders it unlocks.
 * XP comes only from completed middleman trades (verified by a shop),
 * 100 each. The border ladder lives in utils/experience.js.
 */
// embedded: rendered inside Account Settings (its tab names it, so no
// page header of its own)
function Experience({ embedded = false }) {
    const { user, setUser } = useUser();
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (!user) return;
        fetch(`/api/users/${user.id}/profile`, { credentials: 'include' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => setProfile(d))
            .catch(() => { });
    }, [user]);

    // Wear an unlocked ring (server validates the unlock)
    const wearBorder = async (key) => {
        try {
            const res = await fetch('/api/user/border', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ border: key }),
            });
            if (res.ok) {
                setUser(prev => (prev ? { ...prev, selected_border: key } : prev));
                setProfile(prev => (prev ? { ...prev, selected_border: key } : prev));
            }
        } catch { }
    };

    if (!user) {
        return <LoginGate title="Experience" message="Log in to see your level and border unlocks." />;
    }

    const xp = Number((profile || user).experience) || 0;
    const level = levelFromXp(xp);
    const isShop = user.role === 'shop';
    const isAdmin = user.role === 'admin';
    const worn = borderForUser(profile || user);
    const floor = xpForLevel(level);
    const ceil = xpForLevel(level + 1);
    const pct = Math.min(100, Math.round(((xp - floor) / (ceil - floor)) * 100));
    const mmTrades = profile?.trades?.middleman;

    // The ring itself always renders in FULL color - locked tiers grey
    // out only the photo inside (via .xp-tier.locked), so every ring's
    // look is apparent before you've earned it
    const avatar = (frame) => (
        <AvatarFrame user={user} border={frame}>
            <img
                src={getProfilePhotoSrc(user)}
                alt=""
                className="xp-preview-img"
                onError={e => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
            />
        </AvatarFrame>
    );

    return (
        <div className={embedded ? 'xp-page xp-embedded' : 'onesearch xp-page'}>
            {!embedded && (
                <header className="page-section-head">
                    <h1>Experience</h1>
                </header>
            )}

            {/* You, now */}
            <div className="xp-hero">
                {avatar(worn)}
                <div className="xp-hero-main">
                    <div className="xp-hero-level">
                        {isAdmin ? 'Admin' : isShop ? 'Verified Shop' : `Level ${level}`}
                        {!isShop && !isAdmin && worn.key !== 'none' && <span className="xp-hero-border">· {worn.name}</span>}
                    </div>
                    <div className="xp-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                        <div className="xp-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="xp-hero-sub">
                        {xp} XP · {ceil - xp} XP to level {level + 1}
                        {mmTrades != null && ` · earned from ${mmTrades} middleman trade${mmTrades !== 1 ? 's' : ''}`}
                    </p>
                    <p className="os-hint">
                        Every completed middleman trade earns {XP_PER_MM_TRADE} XP. A shop verified
                        both sides, so it's the trade that counts. Direct trades show on your profile
                        but don't earn XP.
                    </p>
                </div>
            </div>

            {/* The ladder - click an unlocked ring to wear it */}
            <h3 className="os-people-sub">Border unlocks</h3>
            <p className="os-hint">Click any unlocked ring to wear it everywhere your photo shows.</p>
            <div className="xp-grid">
                {(() => {
                    const me = profile || user;
                    const verified = isVerified(me);
                    const tiers = [
                        ...BORDERS.map(b => ({ b, unlocked: isAdmin || level >= b.level, req: b.level === 1 ? 'Default' : `Level ${b.level}` })),
                        { b: VERIFIED_BORDER, unlocked: isAdmin || verified, req: verified ? 'Verified ✓' : 'Verify email + phone' },
                        { b: SHOP_BORDER, unlocked: isAdmin || isShop, req: isShop ? 'Yours ✓' : 'Shop exclusive' },
                        { b: ADMIN_BORDER, unlocked: isAdmin, req: isAdmin ? 'Yours ✓' : 'Admin exclusive' },
                    ];
                    return tiers.map(({ b, unlocked, req }) => {
                        const wearing = worn.key === b.key;
                        return (
                            <div key={b.key} className={`xp-tier${unlocked ? '' : ' locked'}`}>
                                {avatar(b)}
                                <div className="xp-tier-name">{b.name}</div>
                                <div className="xp-tier-req">{unlocked && b.level > 1 ? `${req} ✓` : req}</div>
                                <div className="xp-tier-blurb">{b.blurb}</div>
                                {unlocked && (
                                    wearing
                                        ? <span className="xp-wearing">Wearing ✓</span>
                                        : <button className="xp-wear-btn" onClick={() => wearBorder(b.key)}>Wear this</button>
                                )}
                            </div>
                        );
                    });
                })()}
            </div>
        </div>
    );
}

export default Experience;
